/**
 * Change History API Client
 *
 * История изменений — перечень групп поверх аудит-лога, отмена и возврат
 * чужих правок (spec/features/changelog-undo-redo/change-history.md).
 *
 * Это второе представление над тем же журналом, что и личный стек отмены
 * (R19): записи одни и те же, различаются вход и правила отмены.
 */

import { httpClient } from "./client";
import type { IUndoRedoResult } from "./changelog";
import {
  assertObject,
  assertString,
  assertNumber,
  assertBoolean,
  assertArrayOf,
  assertStringOrNullish,
  assertOptionalString,
  assertOptionalNumber,
} from "../types/validation";

// ============================================================================
// Types
// ============================================================================

/** Область изменения — по ней проверяются права просмотра и отмены (rbac-R34). */
export type TChangeArea =
  | "business_model"
  | "mapping"
  | "notes_and_tags"
  | "dictionaries"
  | "datamarts";

export const CHANGE_AREAS: readonly TChangeArea[] = [
  "business_model",
  "mapping",
  "notes_and_tags",
  "dictionaries",
  "datamarts",
];

/** Канал изменения: интерфейс либо канал AI-агента (R02). */
export type TChangeChannel = "ui" | "mcp";

export const CHANGE_CHANNELS: readonly TChangeChannel[] = ["ui", "mcp"];

/** Автор действия или операции над группой. */
export interface IChangeActor {
  readonly id: number;
  readonly login: string;
  readonly fullName?: string;
}

/** Строка перечня — одна группа, то есть одно пользовательское действие (R01). */
export interface IChangeGroupSummary {
  readonly groupId: string;
  /** Момент группы: по нему идут порядок и отбор периодом (R05, R08). */
  readonly performedAt: string;
  readonly author?: IChangeActor;
  readonly channel: TChangeChannel;
  /** Охват: сколько действий в группе. */
  readonly actionCount: number;
  /** Охват: какие виды сущностей затронуты. */
  readonly entityKinds: readonly string[];
  /** Область группы — объединение областей её действий (rbac-R34). */
  readonly areas: readonly TChangeArea[];
  readonly isUndone: boolean;
  /** След последней операции над группой: кто и когда отменил или вернул (R14). */
  readonly undoneBy?: IChangeActor;
  readonly undoneAt?: string;
}

/** Изменение одного поля внутри действия (R03). */
export interface IChangeFieldDiff {
  readonly field: string;
  readonly oldValue?: unknown;
  readonly newValue?: unknown;
}

/** Одно действие группы при её раскрытии (R03, R04). */
export interface IChangeActionView {
  readonly entityKind: string;
  readonly objectId: number;
  /** Актуальное название; его отсутствие — объект удалён либо не разрешается (R04). */
  readonly objectName?: string;
  /**
   * Подпись объекта без собственного названия — системного узла конвейера
   * (mapping-pipeline-R03). Признак машиночитаемый, текст собирает интерфейс.
   */
  readonly objectCaption?: string;
  /** `create` | `update` | `delete`; иное значение — расхождение реализации. */
  readonly operation: string;
  readonly area?: TChangeArea;
  readonly fields: readonly IChangeFieldDiff[];
}

/** Раскрытая группа: строка перечня и её действия. */
export interface IChangeGroupDetails extends IChangeGroupSummary {
  readonly actions: readonly IChangeActionView[];
}

/** Срез перечня (R06). */
export interface IChangeHistorySlice {
  readonly groups: readonly IChangeGroupSummary[];
  /** Срез упёрся в предел: показаны последние 200, есть и более ранние. */
  readonly truncated: boolean;
  /** Размер страницы интерфейса. */
  readonly pageSize: number;
}

/** Фильтры перечня (R08). Пустое поле — без ограничения. */
export interface IChangeHistoryFilter {
  readonly from?: string;
  readonly to?: string;
  readonly authorIds?: readonly number[];
  readonly channels?: readonly TChangeChannel[];
  readonly areas?: readonly TChangeArea[];
  /** Объект задаётся парой: вид сущности и её идентификатор (R08, R09). */
  readonly entityKind?: string;
  readonly objectId?: number;
  /**
   * «Мой текущий сеанс» (R08, основание: вердикт 240): только правки сеанса
   * редактирования этой вкладки.
   *
   * Признак, а не идентификатор: какой сеанс имеется в виду, определяет
   * продукт по заголовку вкладки. Без открытого сеанса признак не предлагается
   * (`edit-sessions.md`), а отдельной поверхности «история сеанса» продукт не
   * имеет.
   */
  readonly mySession?: boolean;
}

// ============================================================================
// Runtime Validators
// ============================================================================

function validateActor(data: unknown, path: string): IChangeActor {
  const obj = assertObject(data, path);
  return {
    id: assertNumber(obj["id"], `${path}.id`),
    login: assertString(obj["login"], `${path}.login`),
    fullName: assertOptionalString(obj["fullName"], `${path}.fullName`),
  };
}

function validateArea(value: unknown, path: string): TChangeArea {
  const area = assertString(value, path);
  if (!CHANGE_AREAS.includes(area as TChangeArea)) {
    throw new Error(`${path}: неизвестная область изменения «${area}»`);
  }
  return area as TChangeArea;
}

function validateChannel(value: unknown, path: string): TChangeChannel {
  const channel = assertString(value, path);
  if (!CHANGE_CHANNELS.includes(channel as TChangeChannel)) {
    throw new Error(`${path}: неизвестный канал изменения «${channel}»`);
  }
  return channel as TChangeChannel;
}

function validateGroupSummary(data: unknown, path: string): IChangeGroupSummary {
  const obj = assertObject(data, path);
  return {
    groupId: assertString(obj["groupId"], `${path}.groupId`),
    performedAt: assertString(obj["performedAt"], `${path}.performedAt`),
    author:
      obj["author"] === undefined || obj["author"] === null
        ? undefined
        : validateActor(obj["author"], `${path}.author`),
    channel: validateChannel(obj["channel"], `${path}.channel`),
    actionCount: assertNumber(obj["actionCount"], `${path}.actionCount`),
    entityKinds: assertArrayOf(
      obj["entityKinds"],
      (item, p) => assertString(item, p),
      `${path}.entityKinds`,
    ),
    areas: assertArrayOf(obj["areas"], validateArea, `${path}.areas`),
    isUndone: assertBoolean(obj["isUndone"], `${path}.isUndone`),
    undoneBy:
      obj["undoneBy"] === undefined || obj["undoneBy"] === null
        ? undefined
        : validateActor(obj["undoneBy"], `${path}.undoneBy`),
    undoneAt: assertStringOrNullish(obj["undoneAt"], `${path}.undoneAt`) ?? undefined,
  };
}

function validateFieldDiff(data: unknown, path: string): IChangeFieldDiff {
  const obj = assertObject(data, path);
  return {
    field: assertString(obj["field"], `${path}.field`),
    oldValue: obj["oldValue"],
    newValue: obj["newValue"],
  };
}

function validateActionView(data: unknown, path: string): IChangeActionView {
  const obj = assertObject(data, path);
  return {
    entityKind: assertString(obj["entityKind"], `${path}.entityKind`),
    objectId: assertNumber(obj["objectId"], `${path}.objectId`),
    objectName: assertOptionalString(obj["objectName"], `${path}.objectName`),
    objectCaption: assertOptionalString(obj["objectCaption"], `${path}.objectCaption`),
    operation: assertString(obj["operation"], `${path}.operation`),
    area:
      obj["area"] === undefined || obj["area"] === null
        ? undefined
        : validateArea(obj["area"], `${path}.area`),
    fields: assertArrayOf(obj["fields"], validateFieldDiff, `${path}.fields`),
  };
}

function validateGroupDetails(data: unknown, path: string): IChangeGroupDetails {
  const summary = validateGroupSummary(data, path);
  const obj = assertObject(data, path);
  return {
    ...summary,
    actions: assertArrayOf(obj["actions"], validateActionView, `${path}.actions`),
  };
}

function validateSlice(data: unknown, path: string): IChangeHistorySlice {
  const obj = assertObject(data, path);
  return {
    groups: assertArrayOf(obj["groups"], validateGroupSummary, `${path}.groups`),
    truncated: assertBoolean(obj["truncated"], `${path}.truncated`),
    pageSize: assertOptionalNumber(obj["pageSize"], `${path}.pageSize`) ?? 50,
  };
}

function validateUndoRedoResult(data: unknown, path: string): IUndoRedoResult {
  const obj = assertObject(data, path);
  return {
    success: assertBoolean(obj["success"], `${path}.success`),
    actionGroupId: assertString(obj["actionGroupId"], `${path}.actionGroupId`),
    // Перечень затронутых сущностей называет доменные виды, а не таблицы
    // хранения (undo-redo-R18, audit-R12) — контракт общий с личным стеком.
    affectedEntityKinds: assertArrayOf(
      obj["affectedEntityKinds"],
      (item, p) => assertString(item, p),
      `${path}.affectedEntityKinds`,
    ),
    affectedRowCount: assertNumber(obj["affectedRowCount"], `${path}.affectedRowCount`),
    canUndo: assertBoolean(obj["canUndo"], `${path}.canUndo`),
    canRedo: assertBoolean(obj["canRedo"], `${path}.canRedo`),
  };
}

// ============================================================================
// Query building
// ============================================================================

/**
 * Строка запроса перечня: множественные значения — через запятую.
 *
 * Пустые фильтры не передаются вовсе: «без ограничения» и «пустой перечень
 * значений» на сервере различаются (R08).
 */
export function buildChangeHistoryQuery(filter: IChangeHistoryFilter): Record<string, string> {
  const query: Record<string, string> = {};

  if (filter.from !== undefined && filter.from.length > 0) query["from"] = filter.from;
  if (filter.to !== undefined && filter.to.length > 0) query["to"] = filter.to;
  if (filter.authorIds !== undefined && filter.authorIds.length > 0) {
    query["authors"] = filter.authorIds.join(",");
  }
  if (filter.channels !== undefined && filter.channels.length > 0) {
    query["channels"] = filter.channels.join(",");
  }
  if (filter.areas !== undefined && filter.areas.length > 0) {
    query["areas"] = filter.areas.join(",");
  }
  if (filter.entityKind !== undefined && filter.objectId !== undefined) {
    query["entityKind"] = filter.entityKind;
    query["objectId"] = String(filter.objectId);
  }
  if (filter.mySession === true) query["mySession"] = "true";

  return query;
}

// ============================================================================
// API Client
// ============================================================================

export interface IChangeHistoryApiClient {
  /** Срез перечня истории проекта (R01–R08). */
  list(projectId: number, filter: IChangeHistoryFilter): Promise<IChangeHistorySlice>;

  /** Раскрытие группы в перечень её действий (R03, R04). */
  getGroup(projectId: number, groupId: string): Promise<IChangeGroupDetails>;

  /** Отмена выбранной группы (R11, R12). */
  undoGroup(projectId: number, groupId: string): Promise<IUndoRedoResult>;

  /** Возврат выбранной группы (R13). */
  redoGroup(projectId: number, groupId: string): Promise<IUndoRedoResult>;
}

export class ChangeHistoryApiClient implements IChangeHistoryApiClient {
  async list(projectId: number, filter: IChangeHistoryFilter): Promise<IChangeHistorySlice> {
    const response = await httpClient.get<unknown>(`/projects/${projectId}/change-history`, {
      params: buildChangeHistoryQuery(filter),
    });
    return validateSlice(response.data, "listChangeHistory");
  }

  async getGroup(projectId: number, groupId: string): Promise<IChangeGroupDetails> {
    const response = await httpClient.get<unknown>(
      `/projects/${projectId}/change-history/${groupId}`,
    );
    return validateGroupDetails(response.data, "getChangeGroup");
  }

  async undoGroup(projectId: number, groupId: string): Promise<IUndoRedoResult> {
    const response = await httpClient.post<unknown>(
      `/projects/${projectId}/change-history/${groupId}/undo`,
    );
    return validateUndoRedoResult(response.data, "undoChangeGroup");
  }

  async redoGroup(projectId: number, groupId: string): Promise<IUndoRedoResult> {
    const response = await httpClient.post<unknown>(
      `/projects/${projectId}/change-history/${groupId}/redo`,
    );
    return validateUndoRedoResult(response.data, "redoChangeGroup");
  }
}

// ============================================================================
// Singleton
// ============================================================================

let changeHistoryApiClient: ChangeHistoryApiClient | null = null;

export function getChangeHistoryApiClient(): ChangeHistoryApiClient {
  changeHistoryApiClient ??= new ChangeHistoryApiClient();
  return changeHistoryApiClient;
}
